/*
 * Product description "See More" / "See Less".
 *
 * Registered as a plain global rather than Alpine.data() so load order never
 * matters: Alpine evaluates x-data expressions against the global scope, and
 * this file is deferred ahead of bundle.js. No external dependencies.
 *
 * The collapsed height is a fixed number of LINES, read from the --pdb-lines
 * custom property, so every product starts at the same height regardless of
 * how long its copy is. Expanding animates max-height between the clamped
 * height and the measured content height so the buy button glides rather
 * than jumping.
 */
(function () {
  'use strict';

  var DURATION = 320;
  var DEFAULT_LINES = 6;

  window.descriptionToggle = function () {
    return {
      open: false,
      ready: false,
      animatable: false,
      overflowing: false,
      resizeHandler: null,
      observer: null,
      transitioning: false,

      init: function () {
        var self = this;

        this.$nextTick(function () {
          self.measure();
        });

        /* Web fonts change the line box, so re-measure once they land. */
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(function () {
            self.measure();
          });
        }

        var timer = null;
        var remeasure = function () {
          clearTimeout(timer);
          timer = setTimeout(function () {
            self.measure();
          }, 150);
        };

        /* ResizeObserver rather than a window resize listener: this snippet
           also renders inside taste panels, which can be display:none at load.
           A hidden element measures as zero height, so without this it would
           never learn that its copy overflows. RO fires when the element gains
           a size, on width changes, and on reflow from late-loading content. */
        if (window.ResizeObserver) {
          this.observer = new ResizeObserver(remeasure);
          this.observer.observe(this.$refs.body);
        } else {
          this.resizeHandler = remeasure;
          window.addEventListener('resize', this.resizeHandler);
        }
      },

      destroy: function () {
        if (this.observer) {
          this.observer.disconnect();
        }
        if (this.resizeHandler) {
          window.removeEventListener('resize', this.resizeHandler);
        }
      },

      /* Clamp height in px = line-height x --pdb-lines. */
      clampHeight: function () {
        var body = this.$refs.body;
        if (!body) return 0;

        var styles = window.getComputedStyle(body);
        var lineHeight = parseFloat(styles.lineHeight);

        /* line-height: normal resolves to NaN — approximate from font-size. */
        if (!lineHeight) {
          lineHeight = parseFloat(styles.fontSize) * 1.45;
        }

        var lines = parseFloat(styles.getPropertyValue('--pdb-lines')) || DEFAULT_LINES;
        return Math.round(lineHeight * lines);
      },

      measure: function () {
        var self = this;
        var body = this.$refs.body;
        if (!body) return;

        /* Never re-measure mid-animation: the ResizeObserver fires on every
           frame of the expand, and releasing the cap here would snap the
           description open instead of letting it glide. */
        if (this.transitioning) return;

        var clamp = this.clampHeight();
        if (!clamp) return;

        body.style.setProperty('--pdb-max', clamp + 'px');
        this.overflowing = body.scrollHeight > clamp + 2;
        this.ready = true;

        /* Content can grow while open (variant switch, late images). */
        if (this.open) {
          body.style.maxHeight = 'none';
        }

        /* Enable the transition just after the clamp lands, so applying the
           clamp on first paint doesn't animate the description shut in front
           of the user. A timer rather than requestAnimationFrame: rAF does not
           run while the tab is in the background, which would leave the
           transition permanently disabled on any product opened in a
           background tab. */
        if (!this.animatable) {
          setTimeout(function () {
            self.animatable = true;
          }, 60);
        }
      },

      toggle: function () {
        if (this.open) {
          this.collapse();
        } else {
          this.expand();
        }
      },

      expand: function () {
        var body = this.$refs.body;
        if (!body) return;

        var self = this;
        this.open = true;
        this.transitioning = true;
        body.style.maxHeight = body.scrollHeight + 'px';

        /* Release the cap afterwards so later reflows are never clipped. */
        this.afterTransition(body, function () {
          body.style.maxHeight = 'none';
          self.transitioning = false;
        });
      },

      collapse: function () {
        var body = this.$refs.body;
        var wrap = this.$el;
        if (!body) return;

        /* Go from 'none' to a concrete height, then to the clamp, so there is
           something for max-height to animate between. */
        body.style.maxHeight = body.scrollHeight + 'px';
        void body.offsetHeight;

        var self = this;
        this.open = false;
        this.transitioning = true;
        body.style.maxHeight = '';
        this.afterTransition(body, function () {
          self.transitioning = false;
        });

        /* Collapsing tall copy can leave the reader stranded below the text. */
        if (wrap && wrap.getBoundingClientRect().top < 0) {
          wrap.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      },

      /* transitionend, with a timeout fallback for reduced-motion and for
         browsers that skip the event when the value doesn't actually change. */
      afterTransition: function (el, done) {
        var finished = false;

        function run() {
          if (finished) return;
          finished = true;
          el.removeEventListener('transitionend', onEnd);
          done();
        }

        function onEnd(event) {
          if (event.target === el && event.propertyName === 'max-height') {
            run();
          }
        }

        el.addEventListener('transitionend', onEnd);
        setTimeout(run, DURATION + 120);
      }
    };
  };
})();
